/**
 * The region maps — the inside of a region, walked a tile at a time
 * (docs/rpg.md §22).
 *
 * One character per tile, as the continent and the towns are authored, and for
 * the same reason: a cottage is obvious in a block of text and invisible in an
 * array of objects.
 *
 * ```
 *   .  grass      #  path        ~  water     _  floor
 *   f  trees      =  bridge      ^  rock      %  wall
 *   "  crop
 * ```
 *
 * These are the third and last scale. The continent shows a student that the
 * calculus is a long way past the arithmetic; a town shows them what is in a
 * kingdom; and a region map shows them the thing a list of three concept links
 * cannot — that the Sum Cottage is a *cottage*, with a hearth on one side and a
 * pantry on the other, and that the Times Table Grove really is trees planted
 * in a rectangle you can walk down both ways.
 *
 * **The picture is the mathematics, wherever it can be.** The grove is seven
 * rows of eight because that is the argument the Orchard Rows quest is about,
 * and turning the field sideways genuinely does not change how many trees are
 * in it. The barn holds two waggons, one of them three times the size of the
 * other, and a heap at the back that would not divide. Nothing here *teaches* —
 * a place is a signpost to a concept page, never a second explanation of it —
 * but a map that draws the right shape has done a lesson a small favour.
 *
 * **A place is a reference and never a second name.** A concept place carries a
 * concept id and nothing else; its title and mastery band are read off the
 * curriculum, so a plate on the map and the card on the kingdom page cannot
 * disagree.
 *
 * **Terrain is scenery and never a lock.** A wall makes a room, so it is also
 * the easiest way in this codebase to fence a concept off by accident:
 * `validateRegionMap` walks every one of these from its arrival tile on every
 * `npm run content:validate`, insists every concept in the region has a place
 * and that every place can be reached, and fails the build otherwise. Every one
 * of these concepts is an ordinary link on the kingdom page as well — the map
 * is the picture, and the cards are the index.
 */

/**
 * **One file per kingdom.** A region map belongs to the kingdom it is inside,
 * and is authored beside that kingdom's village in a single file under `maps/`
 * — the two scales have to agree (a signpost drawn as a mill opens onto a
 * mill), and holding them apart in two thousand-line files is how they stop
 * agreeing. This barrel gathers them; a region without a map is simply absent,
 * and its card on the kingdom page is what it always was.
 */

import type { RegionMap } from '@yuan-math/world';
import { STARTER_VILLAGE_REGION_MAPS } from './maps/starter-village.ts';
import { NUMBER_REGION_MAPS } from './maps/number.ts';
import { FRACTION_REGION_MAPS } from './maps/fraction.ts';
import { ALGEBRA_REGION_MAPS } from './maps/algebra.ts';
import { GEOMETRY_REGION_MAPS } from './maps/geometry.ts';
import { STATISTICS_REGION_MAPS } from './maps/statistics.ts';
import { FUNCTION_REGION_MAPS } from './maps/function.ts';
import { LINEAR_ALGEBRA_REGION_MAPS } from './maps/linear-algebra.ts';
import { TRIGONOMETRY_REGION_MAPS } from './maps/trigonometry.ts';
import { CALCULUS_REGION_MAPS } from './maps/calculus.ts';

/** Every surveyed region. `regionMapOf` picks one; a region without one is a card. */
export const REGION_MAPS: readonly RegionMap[] = [
  ...STARTER_VILLAGE_REGION_MAPS,
  ...NUMBER_REGION_MAPS,
  ...FRACTION_REGION_MAPS,
  ...ALGEBRA_REGION_MAPS,
  ...GEOMETRY_REGION_MAPS,
  ...STATISTICS_REGION_MAPS,
  ...LINEAR_ALGEBRA_REGION_MAPS,
  ...FUNCTION_REGION_MAPS,
  ...TRIGONOMETRY_REGION_MAPS,
  ...CALCULUS_REGION_MAPS,
];
